/**
 * Report Naming Utilities
 * 
 * Centralized logic for generating consistent report names across the application.
 * This ensures all reports follow the standardized naming convention:
 * - Payroll Reports: "Payroll Report - {Report Type} - {Area}"
 * - Service Based Reports: "Service Based Report - {Area}"
 */

// Area value constants
const AREA_VALUES = {
  KIAWAH: 'KIAWAH',
  SEABROOK_ISLAND: 'SEABROOK_ISLAND',
  ISLE_OF_PALMS: 'ISLE_OF_PALMS',
  ISLE_OF_PALMS_WILD_DUNES: 'ISLE_OF_PALMS_WILD_DUNES'
};

/**
 * Maps selected areas to a display name according to business rules:
 * - Kiawah + Seabrook = "Kiawah"
 * - Isle of Palms + Isle of Palms Wild Dunes = "IOP"
 * - All areas or no areas = "All Areas"
 * 
 * @param {string[]} selectedAreas - Array of area values (e.g., ['KIAWAH', 'SEABROOK_ISLAND'])
 * @returns {string} Display name for the area(s)
 */
export function getAreaDisplayName(selectedAreas = []) {
  // No areas selected or all areas selected = "All Areas"
  if (!selectedAreas || selectedAreas.length === 0 || selectedAreas.length === 4) {
    return 'All Areas';
  }

  const hasKiawah = selectedAreas.includes(AREA_VALUES.KIAWAH);
  const hasSeabrook = selectedAreas.includes(AREA_VALUES.SEABROOK_ISLAND);
  const hasIOP = selectedAreas.includes(AREA_VALUES.ISLE_OF_PALMS);
  const hasIOPWildDunes = selectedAreas.includes(AREA_VALUES.ISLE_OF_PALMS_WILD_DUNES);

  // Check for Kiawah group (Kiawah + Seabrook)
  const isKiawahGroup = hasKiawah && hasSeabrook;
  
  // Check for IOP group (Isle of Palms + Isle of Palms Wild Dunes)
  const isIOPGroup = hasIOP && hasIOPWildDunes;

  // Both groups selected = All Areas
  if (isKiawahGroup && isIOPGroup) {
    return 'All Areas';
  }

  // Kiawah group only
  if (isKiawahGroup && !hasIOP && !hasIOPWildDunes) {
    return 'Kiawah';
  }

  // IOP group only
  if (isIOPGroup && !hasKiawah && !hasSeabrook) {
    return 'IOP';
  }

  // If we have a mix or partial selection, return "All Areas" as fallback
  // (based on user requirement that they always select grouped areas)
  return 'All Areas';
}

/**
 * Gets the display name for a report type based on type and role filter
 * 
 * @param {string} reportType - Report type enum value (e.g., 'HOUSEKEEPING', 'INSPECTOR_PROPERTY_MANAGER')
 * @param {string} roleFilter - Role filter enum value (e.g., 'ALL', 'INSPECTOR', 'PROPERTY_MANAGER')
 * @returns {string} Display name for the report type
 */
export function getReportTypeDisplayName(reportType, roleFilter = 'ALL') {
  if (reportType === 'HOUSEKEEPING') {
    return 'Housekeeping';
  }

  if (reportType === 'INSPECTOR_PROPERTY_MANAGER') {
    switch (roleFilter) {
      case 'INSPECTOR':
        return 'Inspector';
      case 'PROPERTY_MANAGER':
        return 'Property Manager';
      case 'ALL':
      default:
        return 'Inspector & PM';
    }
  }

  // Fallback for any other report types
  return reportType.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
}

/**
 * Generates a complete name for a payroll report
 * Format: "Payroll Report - {Report Type} - {Area}"
 * 
 * @param {string} reportType - Report type enum value
 * @param {string} roleFilter - Role filter enum value
 * @param {string[]} selectedAreas - Array of selected area values
 * @returns {string} Complete report name
 */
export function generatePayrollReportName(reportType, roleFilter = 'ALL', selectedAreas = []) {
  const reportTypeDisplay = getReportTypeDisplayName(reportType, roleFilter);
  const areaDisplay = getAreaDisplayName(selectedAreas);
  
  return `Payroll Report - ${reportTypeDisplay} - ${areaDisplay}`;
}

/**
 * Generates a complete name for a service based report
 * Format: "Service Based Report - {Area}"
 * 
 * @param {string[]} selectedAreas - Array of selected area values
 * @returns {string} Complete report name
 */
export function generateServiceReportName(selectedAreas = []) {
  const areaDisplay = getAreaDisplayName(selectedAreas);
  
  return `Service Based Report - ${areaDisplay}`;
}

/**
 * Generates a complete name for a work orders report
 * Format: "Work Orders Report - {Area}"
 * 
 * @param {string[]} selectedAreas - Array of selected area values
 * @returns {string} Complete report name
 */
export function generateWorkOrderReportName(selectedAreas = []) {
  const areaDisplay = getAreaDisplayName(selectedAreas);
  
  return `Work Orders Report - ${areaDisplay}`;
}

